import { useLazyQuery, useQuery } from "@apollo/client";
import HoverTooltip from "@deltek/specification-client/core/Feedbacks/HoverTooltip";
import { ProjectElementType } from "@deltek/specification-client/core/Models/projectElement";
import GridContainer from "@deltek/specification-client/core/Surfaces/GridContainer";
import GridItem from "@deltek/specification-client/core/Surfaces/GridItem";
import { truncateString } from "@deltek/specification-client/core/Utilities/stringUtils";
import { pxToRem } from "@deltek/specification-client/core/Utilities/unitUtils";
import { Button, makeStyles, Popover, Typography } from "@material-ui/core";
import { CategoriesQuery } from "api/graphQL/queries/category.queries";
import { GetProjectElementNodesQuery } from "api/graphQL/queries/projectElements.queries";
import { CategoryPanel, CategoryPanelResponse, ProfessionalDisciplineInner } from "models/category";
import { GetProjectElementNodesInput } from "models/getProjectElementNodesInput";
import { GetProjectElementNodesResponse } from "models/projectElementNode";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

type CategoryFilterButtonProps = {
    category: string;
    onCategoryClick?: (categoryName: string) => void;
}

const useStyles = makeStyles(() => ({
    categoryButton: {
        backgroundColor: 'transparent',
        color: "#FFF",
        border: '1px solid #FFF',
        padding: "6px 12px",
        width: 'auto',
        minWidth: pxToRem(150),
        height: pxToRem(41),
        fontSize: pxToRem(18),
        lineHeight: pxToRem(16),
        borderRadius: 100,
    },
}));

const CategoryOption = ({ category, onCategoryClick, selected }: {
    category: string;
    onCategoryClick: (category: string) => void;
    selected?: boolean;
}) => {
    return (<Typography style={{ padding: '1rem 2rem', backgroundColor: selected ? 'rgba(0,0,0,0.1)' : '#FFF' }} onClick={() => onCategoryClick(category)}>{category.split("/")[0]}</Typography>)
}

const CategoryFilterButton = ({ category, onCategoryClick }: CategoryFilterButtonProps) => {

    const { categoryButton } = useStyles()
    const history = useHistory()
    const location = useLocation()
    const [selectedCategory, setSelectedCategory] = useState<string>(category)
    const [isCategoryPopperOpen, setIsCategoryPopperOpen] = useState(false)
    const [
        popoverAnchorEl,
        setPopoverAnchorEl,
    ] = useState<HTMLButtonElement | null>(null)
    const [allDisciplineData, setData] = useState<
        ProfessionalDisciplineInner[]
    >([])
    const [categoriesData, setCategoriesData] = useState<CategoryPanel[]>([])
    const lastUpdateRef = useRef<string>('')

    useQuery<CategoryPanelResponse>(
        CategoriesQuery,
        {
            fetchPolicy: 'no-cache',
            variables: {
                id: '4e3d44c1-ec50-40c3-8783-1983a905a8a9',
            },
            onCompleted: (data: CategoryPanelResponse) => {
                let dataDiscipline: ProfessionalDisciplineInner[] = []
                dataDiscipline = data?.firm?.professionalDisciplines
                dataDiscipline = [
                    dataDiscipline[dataDiscipline.length - 1],
                    ...dataDiscipline,
                ]

                dataDiscipline.splice(dataDiscipline.length - 1, 1)
                setData(dataDiscipline)

                // Set categories data from the first discipline
                if (dataDiscipline.length > 0) {
                    setCategoriesData(dataDiscipline[0].categories)
                }
            },
        }
    )

    const projectId = `${location.pathname.split('projects/')[1]?.split("/dialog")[0]}`

    const [getProjectElementNodes, { data: projectElementNodeData }] = useLazyQuery<
        GetProjectElementNodesResponse | null,
        GetProjectElementNodesInput
    >(GetProjectElementNodesQuery, {
        fetchPolicy: 'no-cache',
        nextFetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
    })


    const handlePopoverClose = useCallback(() => {
        setPopoverAnchorEl(null)
        setIsCategoryPopperOpen(false)

    }, [])

    const handleCategoryButtonClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            const button = event.currentTarget

            setPopoverAnchorEl(button)
            setIsCategoryPopperOpen(true)
        },
        [handlePopoverClose]
    )

    const handleCategoryOptionClick = useCallback(
        (categoryName: string) => {
            setSelectedCategory(categoryName)
            handlePopoverClose()

            onCategoryClick?.(categoryName)

            // Find the category object and send to parent WorkspacePage
            if (allDisciplineData.length > 0) {
                let selectedCategory: CategoryPanel

                if (categoryName === "All Categories") {
                    selectedCategory = {
                        name: "All Categories",
                        id: "all"
                    }
                } else {
                    const foundCategory = categoriesData.find(cat => cat.name === categoryName)
                    if (foundCategory) {
                        selectedCategory = foundCategory
                    } else {
                        return // Category not found, exit early
                    }
                }

                // Use the first discipline as default
                const selectedDiscipline = allDisciplineData[0]
                getProjectElementNodes({
                    variables: {
                        input: {
                            projectId,
                            categoryId: selectedCategory.id === "all" ? undefined : selectedCategory.id,
                            limit: 1,
                            skip: 0,
                            projectElementType: Object.keys(
                                ProjectElementType
                            ) as ProjectElementType[],
                            onlyElementsAssignedToProject: false,
                            parentNodeId: 'root',
                            isProductFamilyGroupView: false,
                        },
                    }
                })
            }
        },
        [handlePopoverClose, allDisciplineData, categoriesData, onCategoryClick, getProjectElementNodes, projectId]
    )

    useEffect(() => {
        if (projectElementNodeData && projectElementNodeData?.getProjectElementNodes?.projectElementNodes?.length > 0) {
            const firstNode = projectElementNodeData.getProjectElementNodes.projectElementNodes[0]

            if (firstNode) {
                const updateKey = `${selectedCategory}-${firstNode.id}`

                // Only update if we haven't already updated with these values
                if (lastUpdateRef.current !== updateKey) {
                    const queryParams = new URLSearchParams(location.search);

                    queryParams.set('category', selectedCategory)
                    queryParams.set('assemblyPath', firstNode.treePath)
                    queryParams.set('nodeId', firstNode.id)
                    queryParams.set('elementId', firstNode.baseElementId)

                    history.replace({
                        pathname: `/projects/${projectId}/dialog`,
                        search: `?${queryParams.toString()}`
                    })

                    lastUpdateRef.current = updateKey
                }
            }
        }
    }, [projectElementNodeData?.getProjectElementNodes?.projectElementNodes, selectedCategory, projectId])


    const selectedText = useMemo(() => `${selectedCategory.split("/")[0]}`, [selectedCategory])

    const truncatedDisplayName = useMemo(() => {
        return truncateString(selectedText, 20)
    }, [selectedText])

    const categoryOptions = useMemo(() => {
        const defaultCategory = { id: "All Categories", name: "All Categories" };
        const categories = allDisciplineData?.[0]?.categories?.sort((a, b) =>
            a.name.localeCompare(b.name)
        ) ?? []

        return [defaultCategory, ...categories];
    }, [
        allDisciplineData
    ])

    return (
        <GridItem>
            <GridContainer
                item
                wrap="nowrap"
                data-testid="categoryButton"
            >
                <HoverTooltip text={selectedText}>
                    <Button
                        disableElevation
                        disableRipple
                        id={selectedText}
                        className={categoryButton}
                        aria-describedby={'category button'}
                        variant="contained"
                        onClick={handleCategoryButtonClick}
                        data-testid={`categoryFilterButton`}
                        endIcon={isCategoryPopperOpen ? <span className="material-icons">keyboard_arrow_up</span> : <span className="material-icons">keyboard_arrow_down</span>}
                    >
                        {truncatedDisplayName}
                    </Button>
                </HoverTooltip>

                <Popover
                    anchorEl={popoverAnchorEl}
                    open={isCategoryPopperOpen}
                    onClose={handlePopoverClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    marginThreshold={7}
                    data-testid={'headerPopover'}
                >
                    {categoryOptions?.map(category => (
                        <CategoryOption
                            key={category.id}
                            category={category.name}
                            onCategoryClick={handleCategoryOptionClick}
                            selected={selectedCategory === category.name}
                        />
                    ))}
                </Popover>
            </GridContainer>
        </GridItem>
    )
}

export default CategoryFilterButton;